/**
 * Lead Factory option lists used by dashboard filters, forms, and binders.
 */
import type { LeadFactorySavedViewScope } from "./dashboard-lead-factory-types.js";

export const leadFactoryBusinessMetricsScope: LeadFactorySavedViewScope = "business_metrics";
export const leadFactorySavedViewStoragePrefix = "county-lead-factory-saved-view-filters";
export const leadFactorySavedViewVisibilities = ["team", "private"] as const;
export type LeadFactorySavedViewVisibility = (typeof leadFactorySavedViewVisibilities)[number];
export const leadFactoryLeadStatuses = [
  "provisional_document_only",
  "needs_classification",
  "needs_extraction",
  "needs_enrichment",
  "needs_review",
  "ready_to_contact",
  "contact_in_progress",
  "follow_up",
  "pi_required",
  "letter_queue",
  "call_queue",
  "suppressed",
  "dead",
  "acquisition_pipeline",
  "closed_won",
  "closed_lost"
] as const;
export const leadFactoryReviewTriageStates = ["overdue", "blocked", "assigned", "ready"] as const;
export const leadFactoryContactReadinessStates = ["ready_for_letter", "ready_for_call", "ready_multi_channel", "needs_skip_trace", "needs_pi"] as const;
export const leadFactoryContactWorkQueues = ["all", "call", "letter", "research", "skip_trace", "pi"] as const;
export const leadFactoryContactWorkLetterKinds = ["all", "standard", "retry"] as const;
export const leadFactoryContactScopes = ["lead_supply", "all"] as const;
export const booleanFilterOptions = ["true", "false"] as const;
export const leadFactoryAcquisitionStages = ["researching", "interested", "offer_pending", "negotiating", "under_contract", "closed_won", "closed_lost", "dead"] as const;
export const leadFactoryAcquisitionWorkQueues = ["all", "research", "offer", "contract", "closing"] as const;
export const leadFactoryOutreachChannels = ["phone", "letter", "email", "skip_trace", "pi", "note"] as const;
export const contactPolicyOverrideRoles = ["manager", "admin", "automation"] as const;
export const contactPolicyTimezone = "America/New_York";
export const contactPolicyAllowedDays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const contactPolicyStartHour = 9;
export const contactPolicyEndHour = 20;
export const skipTraceResultStatuses = ["phone_found", "email_found", "mailing_address_found", "no_contact_found", "owner_deceased", "needs_pi_review"] as const;
export const piResultStatuses = ["owner_located", "death_confirmed", "heir_identified", "probate_found", "no_owner_contact", "needs_skip_trace"] as const;
export const propertyAddressResearchSources = ["tax_record", "parcel_record", "county_gis", "pi_report", "skip_trace", "alternate_source"] as const;
export const leadFactoryOutreachOutcomes = [
  "attempted",
  "connected",
  "interested",
  "left_message",
  "sent",
  "returned_mail",
  "bad_contact",
  "needs_follow_up",
  "not_interested",
  "do_not_contact",
  "pi_needed",
  "skip_trace_needed"
] as const;
export const leadFactoryOfferStrategies = ["cash_as_is", "subject_to_review", "low_equity_review"] as const;
export const leadFactoryOwnedPropertyTaskStatuses = ["open", "assigned", "blocked", "completed", "cancelled"] as const;
export const leadInventorySortOptions = ["updated_at", "created_at", "priority", "mortgage_amount", "owner_name"] as const;
export const reviewQueueSortOptions = ["triage", "priority", "due_at", "created_at"] as const;
export const contactQueueSortOptions = ["updated_at", "priority", "readiness", "owner_name"] as const;
export const workQueueSortOptions = ["priority", "due_at", "created_at", "owner_name"] as const;
export const acquisitionPipelineSortOptions = ["stage", "updated_at", "mortgage_amount", "owner_name"] as const;
export const assetWorkSortOptions = ["priority", "due_at", "created_at", "task_type"] as const;
export const assetDispositionSortOptions = ["updated_at", "status", "strategy", "target_price"] as const;
export const savedViewSortDirections = ["asc", "desc"] as const;
export const leadFactoryOwnedPropertyTaskTypes = [
  "secure_property",
  "transfer_utilities",
  "bind_insurance",
  "rehab_scope",
  "valuation_update",
  "disposition_strategy"
] as const;
export const leadFactoryOwnedPropertyDispositionStatuses = ["review_open", "strategy_selected", "listed", "under_contract", "disposed", "cancelled"] as const;
export const leadFactoryOwnedPropertyDispositionStrategies = ["hold", "wholesale", "flip", "rental", "resale"] as const;
export const leadFactoryDocumentClasses = [
  "deed_of_trust",
  "appointment_of_substitute_trustee",
  "substitution_of_trustee",
  "notice_of_foreclosure",
  "trustee_related_other",
  "duplicate_document",
  "invalid_pdf",
  "not_relevant",
  "unknown"
] as const;
export const leadFactoryManualFactFields = [
  "owner_name",
  "borrower_name",
  "property_address",
  "parcel_id",
  "mortgage_amount",
  "original_loan_amount",
  "original_loan_date",
  "book",
  "page",
  "document_date",
  "recording_date",
  "owner_age",
  "death_status",
  "heir_count",
  "probate_status"
] as const;
export type LeadFactoryManualFactField = (typeof leadFactoryManualFactFields)[number];
export type LeadFactoryManualFactValueType = "text" | "money" | "date" | "number" | "status";
export const leadFactoryManualFactValueTypes: Record<LeadFactoryManualFactField, LeadFactoryManualFactValueType> = {
  owner_name: "text",
  borrower_name: "text",
  property_address: "text",
  parcel_id: "text",
  mortgage_amount: "money",
  original_loan_amount: "money",
  original_loan_date: "date",
  book: "text",
  page: "text",
  document_date: "date",
  recording_date: "date",
  owner_age: "number",
  death_status: "status",
  heir_count: "number",
  probate_status: "status"
};
export const leadFactoryReviewOutcomes = [
  "classification_confirmed",
  "extraction_confirmed",
  "evidence_confirmed",
  "needs_rework",
  "duplicate_reviewed",
  "not_relevant",
  "invalid_pdf",
  "other_typed_review"
] as const;
